import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { DatePicker, TimePicker } from "./SimplePickers";
import { usePageContent, PageEditor } from "../components/shared/PageEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULTS = {
  bg_image: "https://customer-assets.emergentagent.com/job_00f587e7-4714-4720-98a9-b623d9c78756/artifacts/q8j5llke_IMG_3864.jpeg",
  hero_title: "BOOK THE OTHERS",
  hero_subtitle: "Gør dit event til noget helt særligt med The Others MC",
  info_title: "HVEM ER VI?",
  info_text: "The Others MC er en motorcykelklub med passion for fællesskab og events. Vi skaber uforglemmelige oplevelser med vores motorcykler og medlemmer.",
  why_title: "HVORFOR BOOKE OS?",
  why_point_1: "Fleksible løsninger til alle events",
  why_point_2: "Unikke motorcykeloplevelser",
  why_point_3: "Erfarne og venlige medlemmer",
};

const SCHEMA = [
  { name: "bg_image", label: "Baggrundsbillede", type: "image" },
  { name: "hero_title", label: "Hovedtitel", type: "text" },
  { name: "hero_subtitle", label: "Undertekst", type: "text" },
  { name: "info_title", label: "Info-blok titel", type: "text" },
  { name: "info_text", label: "Info-blok tekst", type: "textarea" },
  { name: "why_title", label: "'Hvorfor' titel", type: "text" },
  { name: "why_point_1", label: "Punkt 1", type: "text" },
  { name: "why_point_2", label: "Punkt 2", type: "text" },
  { name: "why_point_3", label: "Punkt 3", type: "text" },
];

const BookPage = () => {
  const { content } = usePageContent("book-os", DEFAULTS);
  const c = { ...DEFAULTS, ...content };
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: "",
    event_date: "",
    start_time: "12:00",
    end_time: "18:00",
    address_street: "",
    address_city: "",
    address_zip: "",
    guest_count: "",
    venue_type: "",
    description: "",
    honeypot: "", // Anti-spam
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const eventTypes = [
    { value: "Konfirmation", icon: "🎓" },
    { value: "Bryllup", icon: "💒" },
    { value: "Polterabend", icon: "🎉" },
    { value: "Velgørenhed", icon: "❤️" },
    { value: "Show", icon: "🎭" },
    { value: "Andet", icon: "📋" },
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Navn er påkrævet";
    if (!formData.email.trim()) newErrors.email = "Email er påkrævet";
    if (!formData.phone.trim()) newErrors.phone = "Telefon er påkrævet";
    if (!formData.event_type) newErrors.event_type = "Vælg eventtype";
    if (!formData.event_date) newErrors.event_date = "Vælg dato";
    if (!formData.start_time) newErrors.start_time = "Vælg starttid";
    if (!formData.end_time) newErrors.end_time = "Vælg sluttid";
    if (!formData.address_street.trim()) newErrors.address_street = "Adresse er påkrævet";
    if (!formData.address_city.trim()) newErrors.address_city = "By er påkrævet";
    if (!formData.address_zip.trim()) newErrors.address_zip = "Postnummer er påkrævet";
    if (!formData.guest_count) newErrors.guest_count = "Antal gæster er påkrævet";
    if (!formData.venue_type) newErrors.venue_type = "Vælg inde/ude";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Udfyld alle påkrævede felter");
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/bookings`, {
        ...formData,
        event_date: new Date(formData.event_date).toISOString(),
        guest_count: parseInt(formData.guest_count),
      });
      setSubmitted(true);
      toast.success("Booking sendt! Tjek din email for bekræftelse.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke sende booking");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div 
        className="min-h-screen pt-20 pb-12 px-4 flex items-center justify-center" 
        data-testid="book-page"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.7)), url('https://customer-assets.emergentagent.com/job_00f587e7-4714-4720-98a9-b623d9c78756/artifacts/q8j5llke_IMG_3864.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      >
        <div className="rounded-xl p-8 text-center max-w-lg backdrop-blur-md bg-black/30 border border-white/10">
          <div className="w-24 h-24 bg-green-600/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-5xl">✓</span>
          </div>
          <h2 className="text-3xl font-['Oswald'] font-bold text-white mb-4 drop-shadow-lg">TAK FOR DIN BOOKING!</h2>
          <p className="text-white text-lg mb-4 drop-shadow">
            Vi har modtaget din forespørgsel og sender en bekræftelse til din email.
          </p>
          <p className="text-gray-300 mb-8 drop-shadow">
            Vi kontakter dig hurtigst muligt for at bekræfte din booking.
          </p>
          <Link to="/" className="chrome-btn px-8 py-4 rounded-lg inline-block text-lg">
            Tilbage til forsiden
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pt-20 pb-12 px-4" 
      data-testid="book-page"
      style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.35)), url('${c.bg_image}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center 60%',
        backgroundAttachment: 'fixed',
      }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <PageEditor pageKey="book-os" schema={SCHEMA} defaults={DEFAULTS} />
          <h1 className="text-4xl sm:text-5xl font-['Oswald'] font-bold chrome-text mb-4">{c.hero_title}</h1>
          <p className="text-xl text-white drop-shadow-lg max-w-2xl mx-auto">
            {c.hero_subtitle}
          </p>
        </div>

        {/* Info Section */}
        <div className="public-card p-6 sm:p-8 mb-8">
          <h2 className="text-2xl font-['Oswald'] font-bold text-white mb-4 drop-shadow">{c.info_title}</h2>
          <p className="text-white drop-shadow mb-4 whitespace-pre-wrap">{c.info_text}</p>
          
          <h3 className="text-xl font-['Oswald'] font-bold text-blue-300 mt-6 mb-3 drop-shadow">{c.why_title}</h3>
          <ul className="text-white drop-shadow space-y-2 mb-4">
            <li className="flex items-center gap-2"><span className="text-blue-400">✓</span> {c.why_point_1}</li>
            <li className="flex items-center gap-2"><span className="text-blue-400">✓</span> {c.why_point_2}</li>
            <li className="flex items-center gap-2"><span className="text-blue-400">✓</span> {c.why_point_3}</li>
          </ul>
          
          <h3 className="text-xl font-['Oswald'] font-bold text-blue-300 mt-6 mb-3 drop-shadow">EVENTS VI KAN BOOKES TIL</h3>
          <div className="flex flex-wrap gap-2">
            {eventTypes.map(type => (
              <span key={type.value} className="bg-blue-600/30 text-white px-3 py-1 rounded-full text-sm drop-shadow">
                {type.icon} {type.value}
              </span>
            ))}
          </div>
        </div>

        {/* Booking Form */}
        <div className="public-card p-6 sm:p-8">
          <h2 className="text-2xl font-['Oswald'] font-bold text-white mb-6 drop-shadow">BOOKING FORMULAR</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Honeypot - hidden from users */}
            <input 
              type="text" 
              name="website" 
              value={formData.honeypot}
              onChange={(e) => setFormData({ ...formData, honeypot: e.target.value })}
              style={{ display: 'none' }}
              tabIndex={-1}
              autoComplete="off"
            />

            {/* Contact Info */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2 drop-shadow font-medium">Fulde navn *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`mc-input w-full ${errors.name ? 'border-red-500' : ''}`}
                  placeholder="Dit fulde navn"
                  data-testid="booking-name-input"
                />
                {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-white mb-2 drop-shadow font-medium">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`mc-input w-full ${errors.email ? 'border-red-500' : ''}`}
                  placeholder="din@email.dk"
                  data-testid="booking-email-input"
                />
                {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
              </div>
            </div>

            <div>
              <label className="block text-white mb-2 drop-shadow font-medium">Telefonnummer *</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className={`mc-input w-full ${errors.phone ? 'border-red-500' : ''}`}
                placeholder="+45 12 34 56 78"
                data-testid="booking-phone-input"
              />
              {errors.phone && <p className="text-red-400 text-sm mt-1">{errors.phone}</p>}
            </div>

            {/* Event Type */}
            <div>
              <label className="block text-white mb-2 drop-shadow font-medium">Eventtype *</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {eventTypes.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, event_type: type.value })}
                    className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2
                      ${formData.event_type === type.value 
                        ? 'bg-blue-600/50 border-blue-400 text-white' 
                        : 'border-white/20 text-gray-300 hover:border-white/40 bg-black/20'
                      }
                    `}
                    data-testid={`event-type-${type.value}`}
                  >
                    <span className="text-2xl">{type.icon}</span>
                    <span className="text-sm font-medium">{type.value}</span>
                  </button>
                ))}
              </div>
              {errors.event_type && <p className="text-red-400 text-sm mt-1">{errors.event_type}</p>}
            </div>

            {/* Date and Time */}
            <div className="grid sm:grid-cols-3 gap-4">
              <DatePicker
                value={formData.event_date}
                onChange={(v) => setFormData({ ...formData, event_date: v })}
                label="Eventdato"
                required
                placeholder="Vælg dato"
                testId="booking-date-input"
                minDate={new Date().toISOString().split('T')[0]}
                error={errors.event_date}
              />
              <TimePicker
                value={formData.start_time}
                onChange={(v) => setFormData({ ...formData, start_time: v })}
                label="Starttid"
                required
                testId="booking-start-time-input"
                error={errors.start_time}
              />
              <TimePicker
                value={formData.end_time}
                onChange={(v) => setFormData({ ...formData, end_time: v })}
                label="Sluttid"
                required
                testId="booking-end-time-input"
                error={errors.end_time}
              />
            </div>

            {/* Address */}
            <div>
              <label className="block text-white mb-2 drop-shadow font-medium">Eventadresse *</label>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <input
                    type="text"
                    value={formData.address_street}
                    onChange={(e) => setFormData({ ...formData, address_street: e.target.value })}
                    className={`mc-input w-full ${errors.address_street ? 'border-red-500' : ''}`}
                    placeholder="Vejnavn og nummer"
                    data-testid="booking-street-input"
                  />
                  {errors.address_street && <p className="text-red-400 text-sm mt-1">{errors.address_street}</p>}
                </div>
                <div>
                  <input
                    type="text"
                    value={formData.address_zip}
                    onChange={(e) => setFormData({ ...formData, address_zip: e.target.value })}
                    className={`mc-input w-full ${errors.address_zip ? 'border-red-500' : ''}`}
                    placeholder="Postnr"
                    data-testid="booking-zip-input"
                  />
                  {errors.address_zip && <p className="text-red-400 text-sm mt-1">{errors.address_zip}</p>}
                </div>
              </div>
              <div className="mt-3">
                <input
                  type="text"
                  value={formData.address_city}
                  onChange={(e) => setFormData({ ...formData, address_city: e.target.value })}
                  className={`mc-input w-full ${errors.address_city ? 'border-red-500' : ''}`}
                  placeholder="By"
                  data-testid="booking-city-input"
                />
                {errors.address_city && <p className="text-red-400 text-sm mt-1">{errors.address_city}</p>}
              </div>
            </div>

            {/* Guest count and Venue */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2 drop-shadow font-medium">Forventet antal gæster *</label>
                <input
                  type="number"
                  value={formData.guest_count}
                  onChange={(e) => setFormData({ ...formData, guest_count: e.target.value })}
                  className={`mc-input w-full ${errors.guest_count ? 'border-red-500' : ''}`}
                  placeholder="F.eks. 50"
                  min="1"
                  data-testid="booking-guest-count-input"
                />
                {errors.guest_count && <p className="text-red-400 text-sm mt-1">{errors.guest_count}</p>}
              </div>

              <div>
                <label className="block text-white mb-2 drop-shadow font-medium">Indendørs / Udendørs *</label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, venue_type: "indoor" })}
                    className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2
                      ${formData.venue_type === "indoor" 
                        ? 'bg-blue-600/50 border-blue-400 text-white' 
                        : 'border-white/20 text-gray-300 hover:border-white/40 bg-black/20'
                      }
                    `}
                    data-testid="venue-indoor-btn"
                  >
                    <span>🏠</span> Indendørs
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, venue_type: "outdoor" })}
                    className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2
                      ${formData.venue_type === "outdoor" 
                        ? 'bg-blue-600/50 border-blue-400 text-white' 
                        : 'border-white/20 text-gray-300 hover:border-white/40 bg-black/20'
                      }
                    `}
                    data-testid="venue-outdoor-btn"
                  >
                    <span>🌳</span> Udendørs
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, venue_type: "both" })}
                    className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2
                      ${formData.venue_type === "both" 
                        ? 'bg-blue-600/50 border-blue-400 text-white' 
                        : 'border-white/20 text-gray-300 hover:border-white/40 bg-black/20'
                      }
                    `}
                    data-testid="venue-both-btn"
                  >
                    <span>🏠🌳</span> Begge
                  </button>
                </div>
                {errors.venue_type && <p className="text-red-400 text-sm mt-1">{errors.venue_type}</p>}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-white mb-2 drop-shadow font-medium">Beskrivelse / Særlige ønsker</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mc-input w-full h-32 resize-none"
                placeholder="Fortæl os mere om dit event, særlige ønsker eller spørgsmål..."
                data-testid="booking-description-input"
              />
            </div>

            {/* Submit Button - Radial Gradient Style */}
            <button
              type="submit"
              disabled={submitting || submitted}
              className="radial-btn w-full py-5 rounded-xl text-xl disabled:opacity-50"
              data-testid="submit-booking-btn"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-3">
                  <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" />
                  Sender...
                </span>
              ) : submitted ? (
                <span className="flex items-center justify-center gap-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Sendt!
                </span>
              ) : (
                <span className="flex items-center justify-center gap-3">
                  SEND BOOKING
                </span>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};


export default BookPage;
